/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { LineChart } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useParams } from 'react-router';

/* Import configuration */
import { PLOTLY_MIN_WIDTH } from '@/config/app';
import { APP_ROUTES } from '@/config/appRoutes';
import { MUENSTER_DEVICE_SENSOR_CONFIG } from '@/config/deviceSensorConfig';
import { createWaterLossDetailKpiConfigs } from '@/config/kpi/detailWater';

/* Import components */
import Api2TimeSeriesPlot from '@/components/api/Api2TimeSeriesPlot';
import KpiCarouselApi2TimeSeries from '@/components/api/KpiCarouselApi2TimeSeries';
import KpiValueApi2TimeSeries from '@/components/api/KpiValueApi2TimeSeries';
import DashboardGrid from '@/components/layout/DashboardGrid';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

/* Import libraries */
import { DeviceSensor } from '@/lib/sensors/catalog/deviceSensor';
import { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';
import { getSensorHistoryAxisRange } from '@/lib/timeSeries/sensorHistoryAxisRange';
import { createMonthlyLossSeries, getCompletedMonthPeriod } from '@/lib/timeSeries/calendarLoss';

function WaterLossDetailTab() {
    const { locale, t } = useI18n();
    const { waterKey } = useParams<{ waterKey: string }>();
    const [selectedChart, setSelectedChart] = useState<
        'sensor-history-3months' | 'sensor-history-12months'
    >('sensor-history-3months');
    const waterSensorCatalog = new DeviceSensorCatalog(MUENSTER_DEVICE_SENSOR_CONFIG);
    const waterSensorKeys = waterSensorCatalog.getVirtualDevices('WL');
    const selectedWaterKey = waterKey && waterSensorKeys.includes(waterKey) ? waterKey : null;
    const selectedWaterDeviceSensor = selectedWaterKey
        ? new DeviceSensor(selectedWaterKey, waterSensorCatalog)
        : null;

    if (!selectedWaterKey || !selectedWaterDeviceSensor) {
        return <Navigate to={APP_ROUTES.waterloss} replace />;
    }

    const waterTimeSeriesSensorName = selectedWaterDeviceSensor.getSensorName();
    const cumulativeLossSensorName = selectedWaterDeviceSensor.getSensorName('WL', 'V_loss');
    const waterSystemName = selectedWaterDeviceSensor.getDeviceName() ?? selectedWaterKey;
    const sensorHistoryMonths = selectedChart === 'sensor-history-3months' ? 3 : 12;
    const kpiConfigs = createWaterLossDetailKpiConfigs({
        deviceSensor: selectedWaterDeviceSensor,
        t,
    });

    if (!waterTimeSeriesSensorName || !cumulativeLossSensorName || kpiConfigs === null) {
        return <Navigate to={APP_ROUTES.waterloss} replace />;
    }

    return (
        <div className="flex h-full min-h-0 flex-col gap-4">
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-4 text-sm font-medium text-base-content/75">
                <span>
                    <strong>{waterSystemName}:</strong> {selectedWaterKey}
                </span>
                <div className="chart-selection" role="group" aria-label={t('Chart selection')}>
                    <button
                        type="button"
                        className={`chart-selection__button ${selectedChart === 'sensor-history-3months' ? 'chart-selection__button--active' : ''}`}
                        aria-pressed={selectedChart === 'sensor-history-3months'}
                        title={t('Show sensor history for 3 months')}
                        onClick={() => setSelectedChart('sensor-history-3months')}
                    >
                        <LineChart className="h-4 w-4" aria-hidden="true" />
                        {t('Sensor history (3 months)')}
                    </button>
                    <button
                        type="button"
                        className={`chart-selection__button ${selectedChart === 'sensor-history-12months' ? 'chart-selection__button--active' : ''}`}
                        aria-pressed={selectedChart === 'sensor-history-12months'}
                        title={t('Show sensor history for 12 months')}
                        onClick={() => setSelectedChart('sensor-history-12months')}
                    >
                        <LineChart className="h-4 w-4" aria-hidden="true" />
                        {t('Sensor history (12 months)')}
                    </button>
                </div>
            </div>
            <DashboardGrid className="min-h-0 flex-1" desktopCols={3} desktopRows={['150px', '1fr', '1fr']} breakpoint="2xl" aria-label={t('Water loss dashboard')}>
                <DashboardGrid.Item as="section" className="dashboard-panel">
                    <KpiValueApi2TimeSeries {...kpiConfigs.waterLossLastDay} />
                </DashboardGrid.Item>

                <DashboardGrid.Item as="section" className="dashboard-panel">
                    <KpiValueApi2TimeSeries {...kpiConfigs.waterLossLast7Days} />
                </DashboardGrid.Item>

                <DashboardGrid.Item as="section" className="dashboard-panel">
                    <KpiCarouselApi2TimeSeries {...kpiConfigs.cumulativeLoss} />
                </DashboardGrid.Item>

                {/* Scrollable panels require a matching dashboard path in src/config/appPageLayout.ts. */}
                <DashboardGrid.Item as="section" className="dashboard-panel" desktopColSpan={3} mobileMinHeight="750px" scrollable>
                    <Api2TimeSeriesPlot
                        from={sensorHistoryMonths === 3 ? 'NOW-3MONTHS' : 'NOW-12MONTHS'}
                        to="NOW"
                        urn={waterTimeSeriesSensorName}
                        includeZeroYAxis
                        legendNames={{
                            [waterTimeSeriesSensorName]: t('Water flow rate {key}', { key: selectedWaterKey }),
                        }}
                        minWidth={PLOTLY_MIN_WIDTH}
                        title={t('Water flow rate {key}', { key: selectedWaterKey })}
                        xAxisTitle={t('Period')}
                        xAxisRange={getSensorHistoryAxisRange(sensorHistoryMonths)}
                        yAxisTitle={t('Flow rate in m³/h')}
                    />
                </DashboardGrid.Item>
                <DashboardGrid.Item as="section" className="dashboard-panel" desktopColSpan={3} mobileMinHeight="400px">
                    <Api2TimeSeriesPlot
                        from={getCompletedMonthPeriod(13).from}
                        to="NOW"
                        urn={cumulativeLossSensorName}
                        chartType="groupedBar"
                        transformDataset={(data) => createMonthlyLossSeries(data, new Date(), locale)}
                        includeZeroYAxis
                        legendNames={{ [cumulativeLossSensorName]: t('Water loss') }}
                        title={t('Monthly water loss')}
                        xAxisTitle={t('Month')}
                        yAxisTitle={t('Water loss in m³')}
                    />
                </DashboardGrid.Item>
            </DashboardGrid>
        </div>
    );
}

export default WaterLossDetailTab;
